import React, { useEffect, useRef, useState } from "react";
import RenderChildren from "./RenderChildren";
import styled from "@emotion/styled";
import { useAppSelector } from "../../store/hooks";
import {
  findTemplateByStoredReference,
  getTemplateLinkedComponent,
  getTemplateSourceComponent,
} from "./utilities/templateHelpers";
import { defaultComponents } from "./interfaces/DefaultComponents";
import {
  getComponentFromStructureMap,
  matchesEditorTarget,
} from "./utilities/componentIdentity";
import "./AccordionComponent.css";
import { ExpandMore } from "@mui/icons-material";
import { InlineComponentsMapProvider } from "./context/InlineComponentsMapContext";

const Div = styled("div")(
  null,
  (props) =>
    function (props: any) {
      return { ...props["data-style"] };
    }
);

export default function AccordionComponent({
  id,
  uuid,
  attributes,
  childElements,
  childWebpageComponentRelations,
}) {
  const editorId = useAppSelector((state) => state.textEditor?.get("id"));
  const component = (useAppSelector((state) =>
    getComponentFromStructureMap(state.builder.structure.components, uuid ?? id)
  ) as any) ?? ({
    id: typeof id === "number" ? id : -1,
    parent_id: null,
    childElements: childElements ?? [],
    childWebpageComponentRelations: childWebpageComponentRelations ?? [],
    sort_order: 0,
    type: "Accordion",
    uuid: uuid ?? null,
    version: 1,
    webpage_id: 0,
    attributes,
  } as any);
  const [backgroundStyles, setBackgroundStyles] =
    useState<React.CSSProperties>();
  const colors = useAppSelector((state) => state.builder.colors);

  const templates = useAppSelector((state) => state.builder.templates);

  const components = useAppSelector(
    (state) => state.builder.structure.components
  );

  const isBuilder = document.getElementById("BuilderContent");
  const [borderColors, setBorderColors] = useState<any>();
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | "auto">(0);

  const [expanded, setExpanded] = React.useState(
    isBuilder
      ? true
      : component.attributes.default_expanded
      ? component.attributes.default_expanded
      : false
  );

  function handleClick() {
    setExpanded(!expanded);
  }

  const sortedHeaderRelations = (
    Array.isArray(childWebpageComponentRelations)
      ? childWebpageComponentRelations
      : []
  )
    .map((relation: any, index: number) => ({ relation, index }))
    .filter(({ relation }) => !!relation)
    .sort((left, right) => {
      const leftSort =
        typeof left.relation?.sort === "number" && Number.isFinite(left.relation.sort)
          ? left.relation.sort
          : Number.MAX_SAFE_INTEGER;
      const rightSort =
        typeof right.relation?.sort === "number" && Number.isFinite(right.relation.sort)
          ? right.relation.sort
          : Number.MAX_SAFE_INTEGER;
      if (leftSort === rightSort) {
        return left.index - right.index;
      }
      return leftSort - rightSort;
    })
    .map(({ relation }) => relation);
  const headerRelationSort =
    sortedHeaderRelations.length > 0 &&
    typeof sortedHeaderRelations[0]?.sort === "number" &&
    Number.isFinite(sortedHeaderRelations[0].sort)
      ? sortedHeaderRelations[0].sort
      : null;
  const headerRelations =
    headerRelationSort == null
      ? sortedHeaderRelations.slice(0, 1)
      : sortedHeaderRelations.filter(
          (relation: any) =>
            typeof relation?.sort === "number" &&
            Number.isFinite(relation.sort) &&
            relation.sort === headerRelationSort
        );

  const renderChildren = () => {
    const matchedTemplate = findTemplateByStoredReference(
      templates,
      component.attributes.template_id
    );
    const accordionComponent =
      getTemplateLinkedComponent(components, matchedTemplate) ??
      getTemplateSourceComponent(matchedTemplate as any);
    const accordionComponentId =
      accordionComponent?.uuid?.trim() ? accordionComponent.uuid : accordionComponent?.id;

    let ComponentToRender;

    if (matchedTemplate && accordionComponent) {
      ComponentToRender = defaultComponents[accordionComponent.type];
      const inlineComponentsMap =
        matchedTemplate.components && typeof matchedTemplate.components === "object"
          ? matchedTemplate.components
          : null;

      return (
        <InlineComponentsMapProvider map={inlineComponentsMap}>
          <ComponentToRender
            key={accordionComponentId}
            {...accordionComponent}
          />
        </InlineComponentsMapProvider>
      );
    }
    return <></>;
  };

  useEffect(() => {
    // Measure the content height
    const measureHeight = () => {
      if (contentRef.current) {
        const fallbackHeight = contentRef.current.scrollHeight || 0;
        const columnElement =
          contentRef.current.querySelector(".col")?.clientHeight;

        const sectionElement =
          contentRef.current.querySelector(".row")?.clientHeight;

        const containerElement = contentRef.current.querySelector(
          ".container.g-0"
        ) as HTMLElement;

        if (sectionElement && !columnElement && containerElement) {
          setContentHeight(sectionElement);
          containerElement.style.height = `${sectionElement}px`;
        } else if (!sectionElement && columnElement && containerElement) {
          containerElement.style.height = `${columnElement}px`;
          setContentHeight(columnElement);
        } else if (sectionElement && columnElement && containerElement) {
          containerElement.style.height = `${Math.max(
            sectionElement,
            columnElement
          )}px`;

          setContentHeight(Math.max(sectionElement, columnElement));
        } else {
          setContentHeight(fallbackHeight);
        }
      }
    };

    // Measure the height initially
    measureHeight();
  }, [contentRef.current, expanded]);

  useEffect(() => {
    const backgroundColor = component.attributes?.color_id
      ? colors?.find((x: any) => x.id === component.attributes?.color_id)?.color
      : component.attributes?.styles.backgroundColor
      ? component.attributes?.styles.backgroundColor
      : "";

    const backgroundColorHover = component.attributes?.hover_color_id
      ? colors?.find((x: any) => x.id === component.attributes?.hover_color_id)
          ?.color
      : component.attributes?.styles[":hover"] &&
        component.attributes?.styles[":hover"].backgroundColor
      ? component.attributes?.styles[":hover"].backgroundColor
      : "";

    const backgroundObject = {
      backgroundColor: `${backgroundColor ? backgroundColor : "none"} ${
        backgroundColor ? "!important" : ""
      }`,
      ":hover": {
        backgroundColor: `${backgroundColorHover} ${
          backgroundColor ? "!important" : ""
        }`,
      },
    };

    setBackgroundStyles(backgroundObject);
  }, [
    component.attributes,
    component.attributes?.color_id,
    component.attributes?.hover_color_id,
    component.attributes?.styles.backgroundColor,
    component.attributes?.styles[":hover"]?.backgroundColor,
    colors,
  ]);

  useEffect(() => {
    // Change border color values

    const borderColorValues = {
      borderTopColor: component.attributes?.borderTopColorId
        ? colors?.find(
            (x: any) => x.id === component.attributes?.borderTopColorId
          )?.color
        : component.attributes?.styles.borderTopColor
        ? component.attributes?.styles.borderTopColor
        : "",
      borderBottomColor: component.attributes?.borderBottomColorId
        ? colors?.find(
            (x: any) => x.id === component.attributes?.borderBottomColorId
          )?.color
        : component.attributes?.styles.borderBottomColor
        ? component.attributes?.styles.borderBottomColor
        : "",
      borderLeftColor: component.attributes?.borderLeftColorId
        ? colors?.find(
            (x: any) => x.id === component.attributes?.borderLeftColorId
          )?.color
        : component.attributes?.styles.borderLeftColor
        ? component.attributes?.styles.borderLeftColor
        : "",
      borderRightColor: component.attributes?.borderRightColorId
        ? colors?.find(
            (x: any) => x.id === component.attributes?.borderRightColorId
          )?.color
        : component.attributes?.styles.borderRightColor,
    }

    setBorderColors(borderColorValues);
  }, [
    component.attributes,
    component.attributes.styles,
    component.attributes?.borderBottomColorId,
    component.attributes?.borderLeftColorId,
    component.attributes?.borderRightColorId,
    component.attributes?.borderTopColorId,
    colors,
  ]);

  const iconPosition =
    component.attributes?.icon_position === "left" ? "left" : "right";
  const iconSizeRaw = component.attributes?.icon_size;
  const iconSizePx =
    typeof iconSizeRaw === "number" && Number.isFinite(iconSizeRaw)
      ? iconSizeRaw
      : Number.parseInt(String(iconSizeRaw ?? "18"), 10) || 18;
  const contentAlign = component.attributes?.align_content;
  const contentAlignItems: React.CSSProperties["alignItems"] =
    contentAlign === "center"
      ? "center"
      : contentAlign === "end"
      ? "flex-end"
      : "flex-start";

  const headerRowStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "10px",
    position: "relative",
    width: "100%",
  };

  const headerContentWrapStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
  };

  const expandIcon = (
    <ExpandMore
      className="accordion-icon"
      style={{
        flexShrink: 0,
        fontSize: `${iconSizePx}px`,
        color: component.attributes?.icon_color ?? "#000",
        transform: `rotate(${expanded ? 180 : 0}deg)`,
        transition: "transform 0.25s ease, color 0.25s ease",
      }}
    />
  );

  const headerChildren = (
    <div style={headerContentWrapStyle}>
      <RenderChildren
        key={id + "children"}
        childWebpageComponentRelations={headerRelations}
      />
    </div>
  );

  return (
    <>
      <Div
        data-style={{
          ...backgroundStyles,
          backgroundColor: backgroundStyles?.backgroundColor
            ? backgroundStyles?.backgroundColor
            : "none",
        }}
        id={id.toString()}
        data-component-type="accordion"
        className={
          (attributes.classes ?? "") +
          " builder-component builder-component__accordion" +
          (matchesEditorTarget({ id, uuid }, editorId)
            ? " builder-component--active"
            : "")
        }
        style={{
          ...attributes.styles,
          display: "flex",
          flexDirection: "column",
          position: "relative",
          borderLeftColor: borderColors?.borderLeftColor,
          borderRightColor: borderColors?.borderRightColor,
          borderTopColor: borderColors?.borderTopColor,
          borderBottomColor: borderColors?.borderBottomColor,
        }}
        onClick={handleClick}
      >
        <div style={headerRowStyle} className="accordion-header">
          {iconPosition === "left" ? (
            <>
              {expandIcon}
              {headerChildren}
            </>
          ) : (
            <>
              {headerChildren}
              {expandIcon}
            </>
          )}
        </div>

        <div
          ref={contentRef}
          className={`accordion-content`}
          style={{
            display: "flex",
            flexDirection: "column",
            transform: `translateY(${expanded ? "20px" : ""})`,
            overflow: "hidden",
            maxHeight: expanded ? contentHeight : 0,
            height: expanded ? contentHeight : 0,
            opacity: expanded ? 1 : 0, // Fade in/out effect
            transition:
              "max-height 0.25s ease, height 0.25s ease, opacity 0.25s ease",
            justifyContent: "flex-start",
            alignItems: contentAlignItems,
          }}
        >
          {renderChildren()}
        </div>
        {/* <style>
          {`
         .accordion-content .container.g-0  {
            height: ${Number(contentHeight) + 5}px!important;
          }
        `}
        </style> */}
      </Div>
    </>
  );
}
